// apps/web/src/features/cargo/role.ts
//
// Role-aware visibility for the Cargo registry (#1231). The MVP gates a few
// affordances on the actor's SYSTEM ROLE NAME rather than a single permission
// code, because the issue specifies the cohorts by role:
//
//   - Import button          → admin, logist_cn
//   - Manual add + edit       → admin, logist_ru, logist_cn
//   - Control indicators block → admin, logist_ru, logist_cn (hidden for
//                                manager and rop regardless of the backend)
//   - Columns #24 / #28       → admin, logist_ru, logist_cn
//
// WHY a role-name helper instead of permission checks: the four cohorts do not
// map cleanly onto a single shared permission code (e.g. `cargo.import` is the
// natural gate for the import button, but the add/indicator/column cohort is
// "any logistic role + admin" which has no single code). Encapsulating the
// rule here keeps the page declarative and the policy unit-testable.
//
// `admin` is also covered by the `'ALL'` permission sentinel, but we derive
// every decision from the role name so the helper is a pure function of
// `user.role.name` and stays independent of how permissions are bundled.

import type { User } from '@magimp/shared';

/** Logistic system roles that own the Cargo registry write/admin surface. */
const LOGISTIC_ROLE_NAMES = new Set(['logist_cn', 'logist_ru']);

/**
 * Roles allowed to see the Statistics view (#1888). `manager` is excluded —
 * the aggregate KPI/charts surface is a logistics/leadership lens, not a
 * manager workflow. The backend gates on `cargo.read`, so this is a UI-only
 * narrowing layered on top of that read grant.
 */
const STATISTICS_ROLE_NAMES = new Set(['admin', 'logist_cn', 'logist_ru', 'rop', 'executive']);

function roleName(user: User | null): string | null {
  return user && user.role ? user.role.name : null;
}

function isAdmin(user: User | null): boolean {
  return roleName(user) === 'admin';
}

function isLogistic(user: User | null): boolean {
  const name = roleName(user);
  return name !== null && LOGISTIC_ROLE_NAMES.has(name);
}

/** admin / logist_ru / logist_cn — the registry write + admin cohort. */
export function isCargoLogisticRole(user: User | null): boolean {
  return isAdmin(user) || isLogistic(user);
}

/** Import button — admin and logist_cn only. */
export function canImportCargo(user: User | null): boolean {
  return isAdmin(user) || roleName(user) === 'logist_cn';
}

/** Manual add + logistics edit affordances — admin / logist_ru / logist_cn. */
export function canManageCargoRegistry(user: User | null): boolean {
  return isCargoLogisticRole(user);
}

/**
 * Control indicators block — admin / logist_ru / logist_cn only; hidden for
 * manager and rop even though the backend computes the buckets within their
 * own visibility scope.
 */
export function canSeeCargoIndicators(user: User | null): boolean {
  return isCargoLogisticRole(user);
}

/**
 * Carrier-payment column (#24) + deletion column (#28) — admin / logist_ru /
 * logist_cn only.
 */
export function canSeeCargoLogisticColumns(user: User | null): boolean {
  return isCargoLogisticRole(user);
}

/**
 * Read-only card carrier-payment projection — logistics/admin retain their
 * registry projection, while `fin_director` receives the explicitly widened
 * full-card projection (#2566). Other readers stay column-parity scoped.
 */
export function canSeeCargoCarrierPaymentInCard(user: User | null): boolean {
  return isCargoLogisticRole(user) || roleName(user) === 'fin_director';
}

/**
 * Statistics view mode (#1888) — admin / logist_cn / logist_ru / rop /
 * executive. Hidden for manager: a `false` result also forces list mode and
 * suppresses the statistics fetch entirely (FR-10).
 */
export function canSeeCargoStatistics(user: User | null): boolean {
  const name = roleName(user);
  return name !== null && STATISTICS_ROLE_NAMES.has(name);
}

/**
 * Frontend-only default-date window applies for logist_ru / logist_cn, who
 * land on a recent-shipments view rather than the full registry. NOT applied
 * for admin (sees everything) nor manager/rop (own rows only).
 */
export function usesCargoDefaultDateWindow(user: User | null): boolean {
  return isLogistic(user);
}
